// SocketContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { io } from 'socket.io-client'

const SocketContext = createContext({
  socket: null,
  isConnected: false,
  isConnecting: false,
  error: null,
})

export const SocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(true)
  const [error, setError] = useState()
  const [tokenExpired, setTokenExpired] = useState(false)
  const [socket, setSocket] = useState(null)

  // Use ref to store socket instance to prevent recreation
  const socketRef = useRef(null)
  const isInitialized = useRef(false)

  useEffect(() => {
    // Prevent double initialization in Strict Mode
    if (isInitialized.current) return

    const socketOptions = {
      path: process.env.REACT_APP_SOCKET_PATH,
      withCredentials: true,
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      forceNew: true,
      query: {
        source: 'admin',
      },
    }

    const socketInstance = io(process.env.REACT_APP_CHAT_SOCKET, socketOptions)
    socketRef.current = socketInstance
    isInitialized.current = true

    const handleConnect = () => {
      setIsConnected(true)
      setIsConnecting(false)
      setError(null)
      setTokenExpired(false)
    }

    const handleDisconnect = () => {
      setIsConnected(false)
      setIsConnecting(false)
    }

    const handleConnectError = (error) => {
      setIsConnected(false)
      setIsConnecting(false)

      if (
        error.message.includes('Authentication') ||
        error.message.includes('Unauthorized') ||
        error.message.includes('AccessTokenHasExpired') ||
        error.message.includes('AccessTokenRevoked') ||
        error.message.includes('VerifyAccessTokenError') ||
        error.message.includes('UserNotFound')
      ) {
        setTokenExpired(true)
        setError(
          'Authentication failed. Please refresh the page or login again.',
        )
      } else {
        setError(`Connection failed: ${error.message}`)
      }
    }

    const handleReconnect = () => {
      setIsConnected(true)
      setIsConnecting(false)
      setError(null)
      setTokenExpired(false)
    }

    const handleReconnectAttempt = () => {
      setIsConnecting(true)
      setError(null)
    }

    // const handleReconnectError = (error) => {
    //   console.error('Socket reconnection error:', error)
    // }

    // Set up connection event listeners
    socketInstance.on('connect', handleConnect)
    socketInstance.on('disconnect', handleDisconnect)
    socketInstance.on('connect_error', handleConnectError)
    socketInstance.on('reconnect', handleReconnect)
    socketInstance.on('reconnect_attempt', handleReconnectAttempt)
    // socketInstance.on('reconnect_error', handleReconnectError)

    setSocket(socketInstance)

    return () => {
      // Remove all listeners
      socketInstance.off('connect', handleConnect)
      socketInstance.off('disconnect', handleDisconnect)
      socketInstance.off('connect_error', handleConnectError)
      socketInstance.off('reconnect', handleReconnect)
      socketInstance.off('reconnect_attempt', handleReconnectAttempt)
      //   socketInstance.off('reconnect_error', handleReconnectError)

      // Disconnect and cleanup
      socketInstance.disconnect()
      socketRef.current = null
      isInitialized.current = false
    }
  }, [])

  return (
    <SocketContext.Provider
      value={{
        socket: socketRef.current,
        isConnected,
        isConnecting,
        error,
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}

export const useSocket = () => {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider')
  }
  return context
}
